import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  PRIVACY_LABEL,
  useSquadre,
  useUpdatePrivacy,
  type LivelloPrivacy,
} from "../../hooks/useFase7";
import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";

const LIVELLI: LivelloPrivacy[] = ["pubblico", "lega", "riservato", "privato"];

const LEVEL_COLOR: Record<LivelloPrivacy, string> = {
  pubblico: "bg-success/20 text-success border-success/40",
  lega: "bg-info/20 text-info border-info/40",
  riservato: "bg-warning/20 text-warning border-warning/40",
  privato: "bg-danger/20 text-danger border-danger/40",
};

export function PrivacyPage() {
  const { user } = useAuth();
  const { data: squadre, isLoading } = useSquadre();
  const updatePrivacy = useUpdatePrivacy();

  // Squadra del presidente corrente (se esiste)
  const miaSquadra = squadre?.find((s) => s.presidente_user_id === user?.id);
  const [selectedSquadra, setSelectedSquadra] = useState<string | null>(null);

  useEffect(() => {
    if (miaSquadra && !selectedSquadra) {
      setSelectedSquadra(miaSquadra.id);
    }
  }, [miaSquadra, selectedSquadra]);

  const squadra = squadre?.find((s) => s.id === selectedSquadra);
  const isOwner = squadra?.presidente_user_id === user?.id;
  const canEdit = isOwner || user?.ruolo_lega === "presidente_lega" || user?.ruolo_lega === "admin";

  return (
    <MobileShell logoSuffix="PRIVACY">
      <h2 className="font-display text-2xl md:text-3xl text-accent tracking-wide">
        {tr("Privacy squadra")}
      </h2>

      <div className="card">
        <p className="text-fg-muted text-sm">
          Scegli quanto rendere visibile della tua squadra agli{" "}
          <strong>{tr("altri partecipanti")}</strong>
          {tr(
            tr(
              ". I ruoli amministrativi (Pres. Lega, Admin, Tesoriere, Mod TM, Mod Mercato) vedono comunque tutto.",
            ),
          )}
        </p>
      </div>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {squadre && squadre.length > 0 && (
        <div className="card">
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-2">
            {tr("Squadra")}
          </label>
          <select
            value={selectedSquadra ?? ""}
            onChange={(e) => setSelectedSquadra(e.target.value || null)}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
          >
            <option value="">{tr("— seleziona —")}</option>
            {squadre.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
                {s.presidente_user_id === user?.id ? tr(" (tua)") : ""} —{" "}
                {etichetta(s.livello_privacy)}
              </option>
            ))}
          </select>
        </div>
      )}

      {squadra && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl text-accent">{squadra.nome}</h3>
            <span className={`pill border ${LEVEL_COLOR[squadra.livello_privacy]}`}>
              {squadra.livello_privacy.toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-2 mt-4">
            {LIVELLI.map((l) => {
              const active = squadra.livello_privacy === l;
              return (
                <button
                  key={l}
                  type="button"
                  disabled={!canEdit || updatePrivacy.isPending}
                  onClick={() =>
                    updatePrivacy.mutate({
                      squadraId: squadra.id,
                      livello: l,
                    })
                  }
                  className={`text-left p-3 rounded-lg border transition ${
                    active
                      ? `${LEVEL_COLOR[l]} font-bold`
                      : "border-primary/20 bg-primary-deep/40 hover:border-accent/40"
                  } ${!canEdit ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <div className="font-bold uppercase text-sm tracking-wider">{l}</div>
                  <div className="text-xs text-fg-muted mt-1">{PRIVACY_LABEL[l]}</div>
                </button>
              );
            })}
          </div>

          {!canEdit && (
            <div className="text-xs text-fg-muted mt-2">
              {tr("Solo il presidente della squadra (o un admin) può modificare il livello.")}
            </div>
          )}

          {updatePrivacy.isError && (
            <div className="text-danger text-sm mt-2">
              Errore aggiornamento: {String(updatePrivacy.error)}
            </div>
          )}
        </div>
      )}

      <div className="card text-xs text-fg-muted space-y-1 overflow-x-auto">
        <div className="font-bold text-fg-muted/80 mb-1">
          {tr("Matrice visibilità (per altri partecipanti):")}
        </div>
        <table className="w-full text-left min-w-[400px]">
          <thead>
            <tr className="text-fg-muted">
              <th className="py-1">{tr("Area")}</th>
              <th>{tr("Pubblico")}</th>
              <th>{tr("Lega")}</th>
              <th>{tr("Riservato")}</th>
              <th>{tr("Privato")}</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            <tr className="border-t border-primary/10">
              <td className="py-1">{tr("Rosa")}</td>
              <td>VIS</td>
              <td>VIS</td>
              <td>{tr("agg.")}</td>
              <td>—</td>
            </tr>
            <tr className="border-t border-primary/10">
              <td className="py-1">{tr("Tesoreria")}</td>
              <td>VIS</td>
              <td>{tr("agg.")}</td>
              <td>{tr("agg.")}</td>
              <td>—</td>
            </tr>
            <tr className="border-t border-primary/10">
              <td className="py-1">{tr("Storico mercato")}</td>
              <td>VIS</td>
              <td>{tr("agg.")}</td>
              <td>{tr("agg.")}</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </MobileShell>
  );
}
